"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import type React from "react";
import { cn } from "../../lib/utils";
import type { SpacingToken } from "./box-classes";
import type { Omittable } from "../../lib/props";

/**
 * Viewport inset, both axes. The horizontal half travels as a custom property
 * because content that must bleed to the popup's edge — a full-width action bar
 * — cancels it with a negative margin and needs the value, not the class.
 *
 * Declaring the property here rather than on the Popup is deliberate: a custom
 * property set on an ancestor loses to one declared on the element itself, so a
 * caller reaching in to override `--viewport-inline-padding` from the Popup has
 * no effect at all. That is what this prop replaces.
 */
const VIEWPORT_PADDING: Record<SpacingToken, string> = {
  none: "py-0 [--viewport-inline-padding:--spacing(0)]",
  "2xs": "py-0.5 [--viewport-inline-padding:--spacing(0.5)]",
  xs: "py-1 [--viewport-inline-padding:--spacing(1)]",
  sm: "py-2 [--viewport-inline-padding:--spacing(2)]",
  base: "py-3 [--viewport-inline-padding:--spacing(3)]",
  md: "py-4 [--viewport-inline-padding:--spacing(4)]",
  lg: "py-6 [--viewport-inline-padding:--spacing(6)]",
  xl: "py-8 [--viewport-inline-padding:--spacing(8)]",
  "2xl": "py-12 [--viewport-inline-padding:--spacing(12)]",
};

export const Popover: typeof PopoverPrimitive.Root = PopoverPrimitive.Root;

export function PopoverTrigger({
  className,
  children,
  ...props
}: PopoverPrimitive.Trigger.Props): React.ReactElement {
  return (
    <PopoverPrimitive.Trigger
      className={className}
      data-slot="popover-trigger"
      {...props}
    >
      {children}
    </PopoverPrimitive.Trigger>
  );
}

export function PopoverPopup({
  children,
  className,
  side = "bottom",
  align = "center",
  sideOffset = 4,
  alignOffset = 0,
  collisionPadding,
  tooltipStyle = false,
  padding,
  anchor,
  portalProps,
  ...props
}: PopoverPrimitive.Popup.Props & Omittable<{
  /**
   * Viewport inset. `none` for a list-shaped popup whose rows run edge to edge
   * and own their own padding — a menu, a selector pool, a preview card.
   */
  padding?: SpacingToken;
  portalProps?: PopoverPrimitive.Portal.Props;
  side?: PopoverPrimitive.Positioner.Props["side"];
  align?: PopoverPrimitive.Positioner.Props["align"];
  sideOffset?: PopoverPrimitive.Positioner.Props["sideOffset"];
  alignOffset?: PopoverPrimitive.Positioner.Props["alignOffset"];
  /** Keep-out inset for collision avoidance, e.g. `{ top: railTop }` so a
   * popup anchored to a tall column never floats above that column's edge. */
  collisionPadding?: PopoverPrimitive.Positioner.Props["collisionPadding"];
  tooltipStyle?: boolean;
  anchor?: PopoverPrimitive.Positioner.Props["anchor"];
}>): React.ReactElement {
  return (
    <PopoverPrimitive.Portal {...portalProps}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionPadding={collisionPadding}
        className="z-50 h-(--positioner-height) w-(--positioner-width) max-w-(--available-width) transition-[top,left,right,bottom,transform] data-instant:transition-none"
        data-slot="popover-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverPrimitive.Popup
          className={cn(
            "relative flex h-(--popup-height,auto) w-(--popup-width,auto) origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding text-popover-foreground shadow-popover outline-none transition-[width,height,scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] has-data-[slot=calendar]:rounded-xl has-data-[slot=calendar]:before:rounded-[calc(var(--radius-xl)-1px)] data-starting-style:scale-98 data-starting-style:opacity-0 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            tooltipStyle &&
              "w-fit text-balance rounded-md text-xs shadow-card before:rounded-[calc(var(--radius-md)-1px)]",
            className,
          )}
          data-slot="popover-popup"
          {...props}
        >
          <PopoverPrimitive.Viewport
            className={cn(
              "relative size-full max-h-(--available-height) overflow-clip px-(--viewport-inline-padding) has-data-[slot=calendar]:p-2 data-instant:transition-none **:data-current:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-ending-style:opacity-0 **:data-previous:data-starting-style:opacity-0 **:data-current:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-current:opacity-100 **:data-previous:opacity-100 **:data-current:transition-opacity **:data-previous:transition-opacity",
              // The padding prop wins. Absent one, a tooltip-style popup takes a
              // tighter inset than the ramp can express — 4px block, 8px inline.
              padding != null
                ? VIEWPORT_PADDING[padding]
                : tooltipStyle
                  ? "py-1 [--viewport-inline-padding:--spacing(2)]"
                  : VIEWPORT_PADDING.md,
              !tooltipStyle && "not-data-transitioning:overflow-y-auto",
            )}
            data-slot="popover-viewport"
          >
            {children}
          </PopoverPrimitive.Viewport>
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

export function PopoverClose({
  ...props
}: PopoverPrimitive.Close.Props): React.ReactElement {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export function PopoverTitle({
  className,
  ...props
}: PopoverPrimitive.Title.Props): React.ReactElement {
  return (
    <PopoverPrimitive.Title
      className={cn("font-semibold text-lg leading-none", className)}
      data-slot="popover-title"
      {...props}
    />
  );
}

export function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props): React.ReactElement {
  return (
    <PopoverPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="popover-description"
      {...props}
    />
  );
}
